import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

type Tone = "error" | "success" | "info" | "warn";

const TONE: Record<Tone, { box: string; icon: string }> = {
  error: { box: "border-danger/40 bg-danger/10 text-danger", icon: "⚠️" },
  success: { box: "border-success/40 bg-success/10 text-success", icon: "✓" },
  warn: { box: "border-warning/40 bg-warning/10 text-warning", icon: "!" },
  info: { box: "border-stroke bg-surfaceHigh text-textSecondary", icon: "ℹ️" },
};

/** Sayfa üstünde kalan, kapatılabilir bildirim şeridi. */
export function Banner({
  tone,
  children,
  onClose,
}: {
  tone: Tone;
  children: ReactNode;
  onClose?: () => void;
}) {
  const t = TONE[tone];
  return (
    <div
      className={`rounded-2xl border px-4 py-3 mb-4 text-sm flex items-start gap-3 ${t.box}`}
    >
      <span className="leading-5">{t.icon}</span>
      <div className="flex-1 min-w-0 break-words">{children}</div>
      {onClose && (
        <button
          className="text-xs font-bold opacity-70 hover:opacity-100 shrink-0"
          onClick={onClose}
        >
          Kapat
        </button>
      )}
    </div>
  );
}

/**
 * Kısa süreli bildirim.
 *
 * Kaydetme gibi sık ve küçük işlemler için: banner kullanılsa her tıklamada
 * sayfa yukarıdan aşağı zıplıyordu.
 */
export function useToast() {
  const [msg, setMsg] = useState<{ text: string; tone: Tone } | null>(null);
  const timer = useRef<number | null>(null);

  const show = useCallback((text: string, tone: Tone = "success") => {
    if (timer.current) window.clearTimeout(timer.current);
    setMsg({ text, tone });
    timer.current = window.setTimeout(() => setMsg(null), 3500);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    []
  );

  const node = msg ? (
    <div className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-[60] pointer-events-none">
      <div
        className={`card px-4 py-3 shadow-2xl text-sm font-semibold border ${
          TONE[msg.tone].box
        }`}
      >
        {TONE[msg.tone].icon} {msg.text}
      </div>
    </div>
  ) : null;

  return { show, node };
}
